import { useTranslation } from 'react-i18next';
import { usePendingDeviceApprovalCount } from '../../hooks/usePendingDeviceApprovals.js';
import {
  usePendingCorrectionCount,
  usePendingErpChangeCount,
  usePendingLeaveCount,
  usePendingLetterCount,
} from '../../hooks/useNavCounts.js';
import { UnreadBadge, type BadgeTone } from './UnreadBadge.js';
import type { NavBadgeKind } from './navItems.js';

/**
 * The count pill on a sidebar row — the rail telling an approver there is work
 * waiting before they open anything.
 *
 * ONE COMPONENT PER BADGE KIND, and the dispatch below mounts exactly one of
 * them. That is not tidiness: a hook called from `Leaf` itself would register
 * an observer for every one of the forty-odd rows on the rail, and a hook
 * called here with `enabled: kind === …` would register all three observers on
 * every badged row and disable two. Mounting by kind means the query behind a
 * badge runs for precisely the row that shows it, and never for a row a role or
 * a feature flag has already removed.
 *
 * All three render `<UnreadBadge>` — same 99+ cut-off, same nothing-at-zero
 * behaviour, same shape as the bell. Each carries its OWN tone now (see the
 * tone note in UnreadBadge.tsx): brand for corrections, warning for leave,
 * neutral `nav` for devices — the handoff's own three hues.
 *
 * `count === null` is "we do not know yet", which is not "there is nothing to
 * review". `UnreadBadge` renders nothing at 0, so an unknown count is simply
 * absent from the row rather than a confident zero (rule 3.3). Absent is the
 * right shape for the unknown HERE, where a dash would be a mark on a 44px row
 * that reads as a count of something; the device-approvals PAGE, which has room
 * to explain itself, draws the em-dash instead.
 */
function QueueBadge({
  count,
  label,
  tone,
}: {
  count: number | null;
  label: string;
  tone: BadgeTone;
}): JSX.Element {
  return <UnreadBadge count={count ?? 0} tone={tone} label={label} className="ms-auto" />;
}

function CorrectionApprovalsBadge(): JSX.Element {
  const { t } = useTranslation();
  const count = usePendingCorrectionCount();
  return (
    <QueueBadge
      count={count}
      tone="brand"
      label={t('nav.badge.pendingCount', '{{n}} waiting for a decision', { n: count ?? 0 })}
    />
  );
}

function LeaveApprovalsBadge(): JSX.Element {
  const { t } = useTranslation();
  const count = usePendingLeaveCount();
  return (
    <QueueBadge
      count={count}
      tone="warning"
      label={t('nav.badge.pendingCount', '{{n}} waiting for a decision', { n: count ?? 0 })}
    />
  );
}

function LetterApprovalsBadge(): JSX.Element {
  const { t } = useTranslation();
  const count = usePendingLetterCount();
  return (
    <QueueBadge
      count={count}
      tone="warning"
      label={t('nav.badge.pendingCount', '{{n}} waiting for a decision', { n: count ?? 0 })}
    />
  );
}

function DeviceApprovalsBadge(): JSX.Element {
  const { t } = useTranslation();
  const count = usePendingDeviceApprovalCount();
  return (
    <QueueBadge
      count={count}
      tone="nav"
      label={t('nav.badge.pendingCount', '{{n}} waiting for a decision', { n: count ?? 0 })}
    />
  );
}

/**
 * The ERP inbound queue.
 *
 * `warning`, like letters and unlike devices: these are changes to how people
 * are PAID, sitting unapplied. A neutral rail tint would put "the ERP wants to
 * re-route forty workers" at the same visual weight as a page link.
 */
function ErpChangesBadge(): JSX.Element {
  const { t } = useTranslation();
  const count = usePendingErpChangeCount();
  return (
    <QueueBadge
      count={count}
      tone="warning"
      label={t('nav.badge.pendingCount', '{{n}} waiting for a decision', { n: count ?? 0 })}
    />
  );
}

export function NavBadge({ kind }: { kind: NavBadgeKind }): JSX.Element | null {
  switch (kind) {
    case 'correctionApprovals':
      return <CorrectionApprovalsBadge />;
    case 'leaveApprovals':
      return <LeaveApprovalsBadge />;
    case 'letterApprovals':
      return <LetterApprovalsBadge />;
    case 'deviceApprovals':
      return <DeviceApprovalsBadge />;
    case 'erpChanges':
      return <ErpChangesBadge />;
    default:
      return null;
  }
}
